import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { getAdminReports, updateReportStatus } from '../../../api/services/reportService';
import { IoAlertCircleOutline, IoCheckmarkCircleOutline, IoTimeOutline, IoChevronBack, IoChevronForward, IoChatboxEllipsesOutline, IoClose } from 'react-icons/io5';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import './Reports.css';

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [statusFilter, setStatusFilter] = useState('OPEN');
  const [searchQuery, setSearchQuery] = useState('');

  // Resolution handling
  const [resolvingReport, setResolvingReport] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAdminReports({ 
        level: 'ADMIN', 
        status: statusFilter, 
        page, 
        size 
      });

      if (response.status === 'success' && response.data) {
        setReports(response.data.content || []);
        setTotalPages(response.data.totalPages || 1);
      }
    } catch (err) {
      setError(err.message || 'Failed to load reports');
      console.error('Error loading reports:', err);
    } finally {
      setLoading(false);
    }
  }, [page, size, statusFilter]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    if (!resolvingReport) return;
    if (!resolutionNotes.trim()) return alert('Please provide resolution notes.');

    try {
      setIsUpdating(true);
      await updateReportStatus(resolvingReport.id, 'RESOLVED', resolutionNotes.trim());
      setResolvingReport(null);
      setResolutionNotes('');
      loadReports(); // Refresh list
    } catch (err) {
      alert(err.message || 'Failed to update report.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Filter reports locally for search (names instead of IDs)
  const filteredReports = useMemo(() => {
    if (!searchQuery.trim()) return reports;
    const q = searchQuery.toLowerCase();
    return reports.filter(r => 
      (r.title && r.title.toLowerCase().includes(q)) ||
      (r.description && r.description.toLowerCase().includes(q)) ||
      (r.propertyName && r.propertyName.toLowerCase().includes(q)) ||
      (r.reporterEmail && r.reporterEmail.toLowerCase().includes(q)) ||
      (r.category && r.category.toLowerCase().includes(q))
    );
  }, [reports, searchQuery]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, { 
        year: 'numeric', 
        month: 'short', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'OPEN':
        return <span className="report-badge status-open"><IoTimeOutline /> Open</span>;
      case 'RESOLVED':
        return <span className="report-badge status-resolved"><IoCheckmarkCircleOutline /> Resolved</span>;
      default:
        return <span className="report-badge">{status}</span>;
    }
  };

  const getCategoryBadge = (category) => {
    return <span className="report-category-tag">{category.replace(/_/g, ' ')}</span>;
  };

  return (
    <div className="reports-view-container">
      <CollapsibleTopPanel id="reports-header-collapsible" defaultOpenOnMobile={false}>
        <div className="reports-header-section">
          <div>
            <h1 className="page-title">Management Reports</h1>
            <p className="page-subtitle">Complaints and issues reported by tenants and guests</p>
          </div>
        </div>

        <div className="reports-controls">
          <div className="reports-search">
            <input
              type="text"
              placeholder="Search by property, reporter, or issue..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="reports-filters">
            <label>Status:</label>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
              <option value="OPEN">Open Reports</option>
              <option value="RESOLVED">Resolved</option>
            </select>
          </div>
        </div>
      </CollapsibleTopPanel>

      {loading ? (
        <div className="reports-loading-state">
          <div className="spinner"></div>
          <p>Fetching reports...</p>
        </div>
      ) : error ? (
        <div className="reports-error-state">
          <IoAlertCircleOutline size={40} />
          <p>{error}</p>
          <button onClick={loadReports}>Retry</button>
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="reports-empty-state">
          <IoCheckmarkCircleOutline size={40} />
          <p>No reports found.</p>
        </div>
      ) : (
        <div className="reports-table-wrapper">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Report Details</th>
                <th>Category</th>
                <th>Property/Context</th>
                <th>Reporter</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReports.map((report) => (
                <tr key={report.id} className="report-row">
                  <td className="report-main-cell" data-label="Report Details">
                    <div className="report-title-text">{report.title}</div>
                    <div className="report-desc-preview">{report.description}</div>
                  </td>
                  <td data-label="Category">{getCategoryBadge(report.category)}</td>
                  <td data-label="Property/Context">
                    <div className="report-context">
                      <strong>{report.propertyName || 'N/A'}</strong>
                      {report.unitNumber && <span className="unit-label">Unit {report.unitNumber}</span>}
                    </div>
                  </td>
                  <td data-label="Reporter">
                    <div className="reporter-info">
                      <span className="email-link">{report.reporterEmail}</span>
                    </div>
                  </td>
                  <td className="date-cell" data-label="Date">{formatDate(report.createdAt)}</td>
                  <td data-label="Status">{getStatusBadge(report.status)}</td>
                  <td data-label="Actions">
                    {report.status === 'OPEN' ? (
                      <button 
                        className="btn-resolve"
                        onClick={() => setResolvingReport(report)}
                      >
                        <IoCheckmarkCircleOutline /> Resolve
                      </button>
                    ) : (
                      <div className="resolution-info" title={report.resolutionNotes}>
                         <IoChatboxEllipsesOutline /> Resolved
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination-controls">
          <button 
            disabled={page === 0} 
            onClick={() => setPage(p => p - 1)}
            className="pagination-btn"
          >
            <IoChevronBack /> Prev
          </button>
          <span className="page-info">Page {page + 1} of {totalPages}</span>
          <button 
            disabled={page >= totalPages - 1} 
            onClick={() => setPage(p => p + 1)}
            className="pagination-btn"
          >
            Next <IoChevronForward />
          </button>
        </div>
      )}

      {/* Resolution Modal Overlay */}
      {resolvingReport && createPortal(
        <div className="res-modal-overlay">
          <div className="res-modal-content">
            <div className="res-modal-header">
              <h3>Resolve Report</h3>
              <button onClick={() => setResolvingReport(null)}><IoClose size={24}/></button>
            </div>
            <form onSubmit={handleUpdateStatus}>
              <div className="res-report-summary">
                <strong>{resolvingReport.title}</strong>
                <p>{resolvingReport.description}</p>
              </div>
              <div className="res-form-group">
                <label>Resolution Notes</label>
                <textarea 
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Describe how this issue was resolved..."
                  required
                />
              </div>
              <div className="res-modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setResolvingReport(null)}>Cancel</button>
                <button type="submit" className="btn-confirm" disabled={isUpdating}>
                  {isUpdating ? 'Updating...' : 'Mark as Resolved'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Reports;
